'use client';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { ConnectedFocusError } from 'focus-formik-error';

import { usePassword } from '@hooks/auth';
import Button from '@repo/ui/src/components/Button';
import { ErrorState, Input } from '@components/Form';

import { AuthViews, initialValues, InputKeys } from '@repo/ui/src/modules/UI/constants';

interface Props {
  setView?: (view: AuthViews) => void;
  setEmail?: (email: string) => void;
}

const ForgotPassword = ({ setView, setEmail }: Props) => {
  const { t } = useTranslation();
  const { trigger } = usePassword('reset');
  const onSubmit = async (
    values: typeof initialValues.forgot,
    { setStatus, resetForm }: FormikHelpers<typeof initialValues.forgot>,
  ) => {
    setStatus(null);
    try {
      await trigger(values);
      setEmail?.(values.username);
      resetForm();
      setView?.(AuthViews.RESET_PASSWORD);
    } catch (e: any) {
      setStatus(e?.response?.data?.message);
    }
  };

  const validationSchema = () =>
    Yup.object().shape({
      [InputKeys.EMAIL]: Yup.string()
        .email(
          t('auth.fields.emailInput.invalidText', undefined, {
            default: 'Invalid email.',
          }),
        )
        .required(
          t('auth.fields.emailInput.requiredText', undefined, {
            default: 'Email is required.',
          }),
        ),
    });

  return (
    <Formik
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      initialValues={initialValues.forgot}
    >
      {({ isSubmitting, status }) => (
        <Form className="tw-mb-7 tw-grid tw-gap-y-5 sm:tw-gap-y-7">
          <ConnectedFocusError />

          <Input
            required
            autoComplete="email"
            id={InputKeys.EMAIL}
            name={InputKeys.EMAIL}
            type="email"
            label={t('auth.fields.emailInput.label', undefined, {
              default: 'Email',
            })}
          />

          <ErrorState text={status} />

          <Button size="large" type="submit" full disabled={isSubmitting}>
            {t('auth.forgot.form.buttonText', undefined, { default: 'Reset Password' })}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default ForgotPassword;
